import React, { FC, useMemo, useState } from 'react';
import { CircularProgress, IconButton, Paper, useTheme } from '@mui/material';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';

import { IAbsenceRequestDTO } from 'src/api/dtos';
import { AbsenceNotesTooltip } from 'src/features/absences/AbsenceNotesTooltip';
import { ArrowRightIcon, Popper, Status, TrashIcon } from 'src/shared/components';
import { DATE_FORMAT, DATE_TIME_FORMAT, DAY_FORMAT } from 'src/shared/constants';
import { NotificationFlagsEnums } from 'src/shared/enums';
import { useTranslate } from 'src/shared/providers';
import { formatTime, isUserAllowedToCancelAbsence, mapAbsenceStatusStyle } from 'src/shared/utils';
import { RootState } from 'src/store/types';
import { getTimeFormat } from 'src/store/user/user.selectors';

import './AbsenceRequestCard.styles.scss';

interface Props {
  absence: IAbsenceRequestDTO;
  cancelling: boolean;
  onCancelling: (notificationId: number) => void;
  className?: string;
}

export const AbsenceRequestCard: FC<Props> = ({ absence, cancelling, onCancelling, className }) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const [cancelConfirmEl, setCancelConfirmEl] = useState<HTMLElement | null>(null);
  const { settings, info } = useSelector((state: RootState) => state.user);
  const timeFormat = useSelector(getTimeFormat);

  const startDay = moment(absence.startDate).format(DAY_FORMAT);
  const startDate = moment(absence.startDate).format(DATE_FORMAT);
  const endDay = moment(absence.endDate).format(DAY_FORMAT);
  const endDate = moment(absence.endDate).format(DATE_FORMAT);
  const startTime = formatTime(absence.startTime, timeFormat, DATE_TIME_FORMAT);
  const endTime = formatTime(absence.endTime, timeFormat, DATE_TIME_FORMAT);

  const statusId =
    absence.statusId === NotificationFlagsEnums.FORWARDED ? NotificationFlagsEnums.INITIAL : absence.statusId;

  const allowCancelling = useMemo(
    () =>
      isUserAllowedToCancelAbsence({
        statusId: absence.statusId,
        startDate: absence.startDate,
        endDate: absence.endDate,
        isPartialDay: absence.isPartialDay,
        allowCancelAfterApproved: !!settings?.allowCancelAfterApproved,
      }),
    [absence, settings],
  );

  const handleStatusCancel = () => {
    setCancelConfirmEl(null);
    onCancelling(absence.notificationId);
  };

  return (
    <Paper elevation={4} className={className}>
      <table className="w-100 absence-request-table">
        <thead style={{ background: theme.palette.mode === 'light' ? '#F7FAFD' : '#47484D' }}>
          <tr>
            <th scope="col">
              <span className="me-2">{absence.absenceTypeName}</span>
              <AbsenceNotesTooltip employeeId={info?.employeeId} notes={absence.notes} />
            </th>
            <th scope="col">
              <Status variant={mapAbsenceStatusStyle[statusId]} title={absence.statusName} />
            </th>
            <th scope="col">
              {allowCancelling && cancelling && <CircularProgress size={16} />}
              {allowCancelling && !cancelling && (
                <>
                  <Popper
                    id={absence.absenceRequestId}
                    anchorEl={cancelConfirmEl}
                    submitText={t('global.yesImSure')}
                    closeText={t('global.no')}
                    onSubmit={handleStatusCancel}
                    onClose={() => setCancelConfirmEl(null)}
                  >
                    <p className="small">{t('absences.areYouSureYouWantToCancelThisRequest')}</p>
                  </Popper>
                  <IconButton
                    aria-describedby={absence.absenceRequestId}
                    className="p-0 delete-absence-request-btn"
                    size="small"
                    color="error"
                    onClick={(e) => setCancelConfirmEl(cancelConfirmEl ? null : e.currentTarget)}
                  >
                    <TrashIcon size={20} />
                  </IconButton>
                </>
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <span className="d-block small text-muted">{startDay}</span>
              <span className="fw-normal date-info mb-0 mt-1">{startDate}</span>
            </td>
            <td>
              {!absence.isPartialDay && (
                <div className="d-flex align-items-center justify-content-center">
                  <ArrowRightIcon color={theme.palette.primary.main} />
                </div>
              )}
            </td>
            {!absence.isPartialDay ? (
              <td>
                <span className="d-block small text-muted">{endDay}</span>
                <span className="d-block fw-normal date-info mb-0 mt-1">{endDate}</span>
              </td>
            ) : (
              <td className="vertical-align-bottom">
                <span className="d-block date-info mb-0 fw-normal" style={{ color: theme.palette.primary.main }}>
                  <span>{startTime}</span> - <span>{endTime}</span>
                </span>
              </td>
            )}
          </tr>
        </tbody>
      </table>
    </Paper>
  );
};
